import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class GlCode extends ModelObjectWithDirtyCheck<GlCode> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * General Ledger Code
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public code: string = "";
    /**
    * General Ledger Code Name
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    public isHidden: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.code = jsonSerializedObject.code;
          this.name = jsonSerializedObject.name;
          this.isHidden = jsonSerializedObject.isHidden;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.code = this.code;
        toRet.name = this.name;
        toRet.isHidden = this.isHidden;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "GlCode.Id cannot be empty"},
            {field: "code", rule: (v: any) => v == null || v.length !== 0 || "Code cannot be empty"},
            {field: "code", rule: (v: any) => v == null || v.length <= 1000 || "Code has too many characters (1000)"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
    ];
}


export class GlCodeOverride extends ModelObject<GlCodeOverride> {

    /**
    * Stock Item Id
    */
    public stockItemId: Guid = Guid.createEmpty();
    /**
    * Outlet Type ID
    */
    public outletTypeId: Guid = Guid.createEmpty();
    private _glCodeId: Guid | null = null;
    /**
    * GL Code ID
    */
    public get glCodeId(): Guid | null { return this._glCodeId }
    public set glCodeId(value: Guid | null) {
      this._glCodeId = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.outletTypeId = Guid.parseOrEmpty(jsonSerializedObject.outletTypeId);
          this.glCodeId = jsonSerializedObject.glCodeId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.glCodeId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.outletTypeId = this.outletTypeId.toString();
        toRet.glCodeId = this.glCodeId == null ? null : this.glCodeId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "GlCodeOverride.StockItemId cannot be empty"},
            {field: "outletTypeId", rule: (v: any) => !v.isEmpty() || "GlCodeOverride.OutletTypeId cannot be empty"},
            {field: "glCodeId", rule: (v: any) => v == null || !v.isEmpty() || "GlCodeOverride.GlCodeId cannot be empty"},
    ];
}


export class GlCodeService extends ServiceBase {

  public async getAllGlCodes(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(GlCode)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.glCodes.map((v: any) => new GlCode(v));;
    return this.makeJsonRequest("glCode/v1/getAllGlCodes", req, false, extractor, opts);
  };

  public async getGlCode(glCodeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<GlCode> {
    const req = {
    tenantId:api.tenantId.toString(),
    glCodeId:glCodeId.toString(),
    };
    const extractor = (response:any) => new GlCode(response.glCode);;
    return this.makeJsonRequest("glCode/v1/getGlCode", req, false, extractor, opts);
  };

  public async getGlCodes(glCodeIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(GlCode)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    glCodeIds:glCodeIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.glCodes.map((v: any) => new GlCode(v));;
    return this.makeJsonRequest("glCode/v1/getGlCodes", req, false, extractor, opts);
  };

  public async saveGlCode(glCode: GlCode, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    glCode:glCode.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("glCode/v1/saveGlCode", req, true, extractor, opts);
  };

  public async setGlCodeIsHidden(glCodeId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    glCodeId:glCodeId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("glCode/v1/setGlCodeIsHidden", req, true, extractor, opts);
  };

  public async getAllGlCodeOverrides(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(GlCodeOverride)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.glCodeOverrides.map((v: any) => new GlCodeOverride(v));;
    return this.makeJsonRequest("glCode/v1/getAllGlCodeOverrides", req, false, extractor, opts);
  };

  public async getGlCodeOverridesForOutletType(outletTypeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(GlCodeOverride)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletTypeId:outletTypeId.toString(),
    };
    const extractor = (response:any) => response.glCodeOverrides.map((v: any) => new GlCodeOverride(v));;
    return this.makeJsonRequest("glCode/v1/getGlCodeOverridesForOutletType", req, false, extractor, opts);
  };

  public async getGlCodeOverridesForGlCode(glCodeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(GlCodeOverride)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    glCodeId:glCodeId.toString(),
    };
    const extractor = (response:any) => response.glCodeOverrides.map((v: any) => new GlCodeOverride(v));;
    return this.makeJsonRequest("glCode/v1/getGlCodeOverridesForGlCode", req, false, extractor, opts);
  };

  public async getGlCodeOverrideForStockItemAndOutletType(stockItemId: Guid, outletTypeId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<GlCodeOverride | null> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockItemId:stockItemId.toString(),
    outletTypeId:outletTypeId.toString(),
    };
    const extractor = (response:any) => response.glCodeOverride == null ? null : new GlCodeOverride(response.glCodeOverride);;
    return this.makeJsonRequest("glCode/v1/getGlCodeOverrideForStockItemAndOutletType", req, false, extractor, opts);
  };

  public async saveSingleGlCodeOverride(expectedStockItemVersion: number | null, glCodeOverride: GlCodeOverride, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    expectedStockItemVersion:expectedStockItemVersion == null ? null : expectedStockItemVersion,
    glCodeOverride:glCodeOverride.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("glCode/v1/saveSingleGlCodeOverride", req, true, extractor, opts);
  };
}

export const glCodeService = new GlCodeService();